import React, { useEffect, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useHistory, useLocation } from "react-router";
import {
  Button,
  Col,
  Divider,
  FormInstance,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Steps,
  Tooltip,
} from "antd";
import type { Moment } from "moment";
import { PlusOutlined } from "@ant-design/icons";
import { OfferCatalog } from "./OfferCatalog/OfferCatalog";
import { OfferListSearch } from "./OfferListSearch/OfferListSearch";
import { OfferListTable } from "./OfferListTable/OfferListTable";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_OFFERING_WRITE } from "../../lib/const/permissions";
import {
  IOfferListSelectMode,
  IOfferListViewMode,
} from "../../containers/OfferContainer/context/types";
import type { Agreement, EmptyFunction, LoadingStatusCode, Offer } from "../../lib/types";
import type { IOfferListSearch } from "../../store/slices/offering/types";
import "./OfferList.less";
import LocalStorageService from "../../services/LocalStorageService";

interface ComponentProps {
  offerListViewMode: IOfferListViewMode; // Изначальный вид (отображение кнопок изменения/создания offerListSelectMode и пр.)
  setOfferListViewMode?: React.Dispatch<React.SetStateAction<IOfferListViewMode>>;
  offerListSelectMode?: IOfferListSelectMode; // Поведение по нажатии на строку (выбор, переход в детали и пр.)
  setOfferListSelectMode?: Dispatch<SetStateAction<IOfferListSelectMode>>;
  selectedOffer?: Offer<string>;
  setSelectedOffer: React.Dispatch<React.SetStateAction<Offer<string> | undefined>>;
  isCatalogOpen: boolean;
  toggleCatalogOpen: EmptyFunction;
  searchListValues: IOfferListSearch<Moment>;
  onChangeSearchValue: (field: Partial<IOfferListSearch<Moment>>) => void;
  onSearch: (paginationProps: { page: number; size?: number }) => void;
  isSearchOpen: boolean;
  toggleSearchOpen: EmptyFunction;
  onResetSearch: EmptyFunction;
  offerList: Offer[];
  offerListTotalElements: number;
  offerListLoading: LoadingStatusCode;
  catalogExpandedKeys: string[];
  setCatalogExpandedKeys: Dispatch<SetStateAction<string[]>>;
  filterName: string; // имя используемое как ключ localStorage для сохранения списка открытых полей поискового фильтра
  disabledSearchFields?: string[];
  selectedAgreement?: Agreement; // По дефолту id договора текстовое поле. В подключении продукта нужно, чтобы было select
  disableOnDateFn?: (date: Moment) => boolean;
  displayPastVersion?: boolean; // В большинстве случае отображение истекших версий не требуется, если нужно - передать true
  isDisabled?: boolean;
  form: FormInstance<any>;
}

export const OfferList = (props: ComponentProps) => {
  const {
    offerListViewMode,
    setOfferListViewMode,
    offerListSelectMode,
    setOfferListSelectMode,
    selectedOffer,
    setSelectedOffer,
    isCatalogOpen,
    toggleCatalogOpen,
    searchListValues,
    onChangeSearchValue,
    onSearch,
    isSearchOpen,
    toggleSearchOpen,
    onResetSearch,
    offerList,
    offerListLoading,
    offerListTotalElements,
    catalogExpandedKeys,
    setCatalogExpandedKeys,
    filterName,
    disabledSearchFields = [],
    selectedAgreement,
    disableOnDateFn,
    displayPastVersion = false,
    isDisabled,
    form,
  } = props;

  const { pathname } = useLocation();

  // Очистка по выходу
  // useEffect(() => () => onResetSearch(), []);

  const history = useHistory();

  const caltalogSpan: number = useMemo(() => (isCatalogOpen ? 4 : 1), [isCatalogOpen]);

  const pageTitle: string = useMemo(() => {
    switch (offerListViewMode) {
      case "view":
        return "Предложения";
      case "createOffer":
        return "Создание предложения";
      case "selectOffer":
        return "Выбор предложения";
      default:
        return "";
    }
  }, [offerListViewMode]);

  const onChangeOfferListSelectMode = (e: RadioChangeEvent) => {
    const value = e.target.value as IOfferListSelectMode;
    if (value === "new") {
      setSelectedOffer(undefined);
    }
    setOfferListSelectMode && setOfferListSelectMode(value);
  };

  const onCancelCreateOffer = () => {
    !!setOfferListViewMode && setOfferListViewMode("view");
    setOfferListSelectMode && setOfferListSelectMode(undefined);
    setSelectedOffer(undefined);
    LocalStorageService.setObject("offerCreateSearchValues");
    history.push("/offer/list");
  };

  // Кнопки выводятся только в режиме создания предложения
  const nextBtnActive = useMemo((): boolean => {
    switch (offerListSelectMode) {
      case "basedOn":
        return Boolean(selectedOffer);
      case "new":
        return Boolean(searchListValues.serviceTypeId);
      default:
        return false;
    }
  }, [offerListSelectMode, selectedOffer, searchListValues.serviceTypeId]);

  const nextBtnTooltip = useMemo((): string => {
    switch (offerListSelectMode) {
      case "basedOn":
        return selectedOffer ? "" : "Необходимо выбрать предложение в результатах поиска";
      case "new":
        return searchListValues.serviceTypeId ? "" : "Необходимо выбрать тип продукта";
      default:
        return "";
    }
  }, [offerListSelectMode, selectedOffer, searchListValues.serviceTypeId]);

  const nextStep = () => {
    const savedObject = { searchListValues, offerListSelectMode, selectedOffer };
    LocalStorageService.setObject("offerCreateSearchValues", JSON.stringify(savedObject));
    history.push(`/offer/create`);
  };

  const onClickAddOffer = () => {
    setOfferListViewMode && setOfferListViewMode("createOffer");
    setOfferListSelectMode && setOfferListSelectMode("new");
    history.push("/offer/list/create");
  };

  useEffect(() => {
    if (pathname.includes("offer/list/create")) {
      setOfferListViewMode && setOfferListViewMode("createOffer");
    }
    if (!pathname.includes("offer/list/create") && pathname.includes("offer/list")) {
      setOfferListViewMode && setOfferListViewMode("view");
    }
  }, [pathname]);

  const getIsDisabledBasedOn = () => {
    let isDisabled = false;

    if (!selectedOffer) {
      isDisabled = true;
    }

    if (
      selectedOffer &&
      selectedOffer.productCharValues &&
      selectedOffer.productCharValues.length > 0
    ) {
      isDisabled = true;
    }

    return isDisabled;
  };

  return (
    <div className="offer-list">
      <Row justify="space-between">
        {pageTitle && <h1>{pageTitle}</h1>}
        {offerListViewMode === "view" && hasAccess(MENU_ADMIN_OFFERING_WRITE) && (
          <Button
            icon={<PlusOutlined />}
            type="primary"
            className="offer-list__add-btn"
            onClick={onClickAddOffer}
          >
            Добавить предложение
          </Button>
        )}
      </Row>
      <Divider className="  offer-list__divider" />
      {offerListViewMode === "createOffer" && (
        <Row justify="space-between" className="offer-list__createSteps">
          <Col flex="auto">
            <Steps size="small" current={0}>
              <Steps.Step title="Выбор предложения" />
              <Steps.Step title="Параметры предложения" />
            </Steps>
          </Col>
          <Col className="offer-list__createSteps-btns">
            <Space size={20}>
              <Button onClick={onCancelCreateOffer}>Назад</Button>
              <Tooltip title={nextBtnTooltip}>
                <Button type="primary" disabled={!nextBtnActive} onClick={nextStep}>
                  Далее
                </Button>
              </Tooltip>
            </Space>
          </Col>
          <Divider />
        </Row>
      )}
      <Row>
        <Col className="offer-list__left" span={caltalogSpan}>
          <OfferCatalog
            isCatalogOpen={isCatalogOpen}
            toggleCatalogOpen={toggleCatalogOpen}
            catalogExpandedKeys={catalogExpandedKeys}
            setCatalogExpandedKeys={setCatalogExpandedKeys}
            onChangeSearchValue={onChangeSearchValue}
            searchListValues={searchListValues}
          />
        </Col>
        <Col className="offer-list__right" span={24 - caltalogSpan}>
          {offerListViewMode === "createOffer" && (
            <div className="offer-list__selectForCreate">
              <h3>Выбор предложения</h3>
              <Radio.Group onChange={onChangeOfferListSelectMode} value={offerListSelectMode}>
                <Radio value="basedOn">Создать на основе</Radio>
                <Radio value="new">Создать новое</Radio>
              </Radio.Group>
            </div>
          )}
          {offerListViewMode === "createProduct" && (
            <div className="offer-list__selectForCreate">
              <h3>Выбор предложения</h3>
              <Radio.Group onChange={onChangeOfferListSelectMode} value={offerListSelectMode}>
                <Radio value="select">Выбрать предложение</Radio>
                <Radio value="basedOn" disabled={getIsDisabledBasedOn()}>
                  Создать на основе
                </Radio>
                <Radio value="new">Создать новое</Radio>
              </Radio.Group>
            </div>
          )}
          {offerListViewMode === "addTemplate" && (
            <div className="offer-list__selectForCreate">
              <h3>Выбор предложения</h3>
              <Radio.Group onChange={onChangeOfferListSelectMode} value={offerListSelectMode}>
                <Radio value="select-template">Выбрать шаблон</Radio>
                <Radio value="skip">Пропустить</Radio>
              </Radio.Group>
            </div>
          )}
          {offerListViewMode === "selectOfferAdd" && (
            <div className="offer-list__selectForCreate">
              <h3>Выбор предложения</h3>
            </div>
          )}
          <div style={{ cursor: isDisabled ? "no-drop" : "" }}>
            <div style={{ pointerEvents: isDisabled ? "none" : undefined }}>
              <OfferListSearch
                isSearchOpen={isSearchOpen}
                toggleSearchOpen={toggleSearchOpen}
                onSearch={onSearch}
                onResetSearch={onResetSearch}
                onChangeSearchValue={onChangeSearchValue}
                // searchListValues={searchListValues}
                form={form}
                offerListLoading={offerListLoading}
                filterName={filterName}
                disabledSearchFields={disabledSearchFields}
                selectedAgreement={selectedAgreement}
                disableOnDateFn={disableOnDateFn}
              />
              <OfferListTable
                filterName={filterName}
                selectedOffer={selectedOffer}
                setSelectedOffer={setSelectedOffer}
                offerListViewMode={offerListViewMode}
                searchListValues={searchListValues}
                onChangeSearchValue={onChangeSearchValue}
                onSearch={onSearch}
                offerList={offerList}
                offerListLoading={offerListLoading}
                offerListTotalElements={offerListTotalElements}
                offerListSelectMode={offerListSelectMode}
                displayPastVersion={displayPastVersion}
                setOfferListSelectMode={setOfferListSelectMode}
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};
